import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import {
    setTicketList,
    ticketListSelector,
    useAppDispatch,
    useAppSelector,
    useStoreTicketMutation,
    useGetTicketGatewaysQuery,
} from '../../../../app/store';
import { TicketFileType } from '../../ticket.types';
import { TextField } from 'components/formik/text-field';
import { handleServerErrors } from 'utils/utils';
import { immediateToast } from 'utils/toast';
import TicketGatewayOption from '../ticket-gateway-option';

const defaultTicketInputError: string =
    'برای ارسال تیکت، حداقل یک متن یا فایل وارد کنید.';

const useNewTicketForm = ({
    onClose,
    onCreated,
    onLoadingChange,
    values,
    setValues,
    validateForm,
    setTouched,
    setSubmitting,
    setStatus,
    resetForm,
}: any) => {
    const dispatch = useAppDispatch();
    const ticketList = useAppSelector(ticketListSelector);

    const [storeTicket, { isLoading: isLoadingStoreTicket }] =
        useStoreTicketMutation();
    const {
        data: ticketGateways = [],
        isFetching: isLoadingTicketGateways,
    } = useGetTicketGatewaysQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    const [sentMessage, setSentMessage] = useState<any>(undefined);
    const [ticketError, setTicketError] = useState<string>('');

    useEffect(() => {
        setValues({ ...values });
    }, []);

    useEffect(() => {
        setSubmitting(isLoadingStoreTicket);
        onLoadingChange?.(isLoadingStoreTicket);
    }, [
        isLoadingStoreTicket,
        onLoadingChange,
        setSubmitting,
    ]);

    useEffect(() => {
        return () => {
            onLoadingChange?.(false);
        };
    }, [onLoadingChange]);

    // handle sending new ticket
    const handleSendTicket = async (
        currentMessage: string,
        setCurrentMessage: React.Dispatch<React.SetStateAction<string>>,
        attachmentData: TicketFileType,
        setAttachmentData: React.Dispatch<
            React.SetStateAction<TicketFileType[]>
        >,
    ) => {
        if (isLoadingStoreTicket) return;

        setTicketError('');

        const formErrors = await validateForm();
        const text = currentMessage.trim();
        const textError =
            text.length === 0
                ? 'فیلد متن تیکت الزامی است'
                : text.length < 3
                  ? 'فیلد متن تیکت باید حداقل 3 کاراکتر باشد'
                  : '';

        if (Object.keys(formErrors ?? {}).length > 0) {
            await setTouched(
                {
                    title: true,
                    department: true,
                    priority: true,
                },
                false,
            );
        }

        if (textError) {
            setTicketError(textError);
        }

        if (Object.keys(formErrors ?? {}).length > 0 || textError) return;

        const files: string[] = attachmentData
            .map((item) =>
                typeof item?.file === 'string' ? item.file : '',
            )
            .filter(Boolean);

        const newMessage = {
            title: values?.title?.trim(),
            gateway_id: values?.gateway_id
                ? Number(values.gateway_id)
                : null,
            department: values?.department,
            priority: values?.priority,
            text,
            files,
        };

        setSentMessage(newMessage);
        handleSendToServer(newMessage);
    };

    // send data to server
    const handleSendToServer = (message: any) => {
        setTicketError('');
        storeTicket(message)
            .unwrap()
            .then((data: any) => {
                dispatch(setTicketList([data, ...(ticketList || [])]));
                onCreated?.(data);
            })
            .catch((error) => {
                handleServerErrors(error, setStatus);
                !!error?.data?.errors?.text &&
                    setTicketError(
                        error?.data?.errors?.text?.[0] ??
                            defaultTicketInputError,
                    );
            });
    };

    // handle resend ticket
    const handleResendTicket = useCallback(() => {
        handleSendToServer(sentMessage);
    }, [handleSendToServer, sentMessage]);

    // handle cancel ticket
    const handleCancelTicket = useCallback(() => {
        resetForm();
        setSentMessage(undefined);
        onClose && onClose();
    }, []);

    // handle error file upload
    const fileSizeLimit = 10;

    const handleSendErrorFile = useCallback(
        (invalidFiles: Array<{ file: File; type: 'size' | 'format' }>) => {
            invalidFiles?.forEach((invalidFile) => {
                immediateToast('error', {
                    timeout: 3000,
                    message:
                        invalidFile.type === 'format'
                            ? `فرمت فایل «${invalidFile.file.name}» مجاز نیست.`
                            : `حجم فایل «${invalidFile.file.name}» نباید بیشتر از ${fileSizeLimit} مگابایت باشد.`,
                });
            });
        },
        [],
    );

    const newTicketFields: Array<any> = [
        {
            name: 'title',
            label: 'موضوع تیکت',
            component: TextField,
            maxLength: 64,
            liveValidation: false,
            clearErrorOnChange: true,
            icon: <i className="fa-light fa-pen" />,
        },
        {
            name: 'gateway_id',
            label: 'درگاه (اختیاری)',
            options: [
                { value: '', label: 'بدون انتخاب درگاه' },
                ...ticketGateways.map((gateway) => ({
                    value: gateway.id,
                    label: <TicketGatewayOption gateway={gateway} />,
                })),
            ],
            component: TextField,
            liveValidation: false,
            clearErrorOnChange: true,
            disabled: isLoadingTicketGateways,
            icon: <i className="fa-light fa-credit-card" />,
        },
        {
            name: 'department',
            label: 'دپارتمان تیکت',
            options: departmentList,
            component: TextField,
            liveValidation: false,
            clearErrorOnChange: true,
            icon: <i className="fa-light fa-building" />,
        },
        {
            name: 'priority',
            label: 'الویت تیکت',
            options: priorityList,
            component: TextField,
            liveValidation: false,
            clearErrorOnChange: true,
            icon: <i className="fa-light fa-bars-staggered" />,
        },
    ];

    return {
        newTicketFields,
        ticketError,
        isSending: isLoadingStoreTicket,
        sentMessage,
        fileSizeLimit,
        onSend: handleSendTicket,
        onResend: handleResendTicket,
        onCancel: handleCancelTicket,
        onErrorFile: handleSendErrorFile,
    };
};

export default useNewTicketForm;

const departmentList = [
    { value: 'info_change', label: 'تایید یا تغییر اطلاعات' },
    { value: 'technical', label: 'موارد فنی' },
    { value: 'financial', label: 'موارد مالی و مغایرت' },
    { value: 'gateway_request', label: 'درخواست درگاه پرداخت' },
    { value: 'authentication', label: 'احراز هویت' },
    { value: 'transaction_tracking', label: 'پیگیری تراکنش' },
    { value: 'merchant_violation', label: 'گزارش تخلف پذیرنده' },
    { value: 'legal', label: 'حقوقی و پیگیری قضایی' },
    { value: 'comment', label: 'پیشنهاد، انتقاد و شکایت' },
];

const priorityList = [
    { value: 'low', label: 'معمولی' },
    { value: 'medium', label: 'مهم' },
    { value: 'high', label: 'بسیار مهم' },
];

export const NewTicketSchema = Yup.object().shape({
    title: Yup.string()
        .required('فیلد موضوع تیکت الزامی است')
        .min(3, 'فیلد موضوع تیکت باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد موضوع تیکت باید حداکثر 64 کاراکتر باشد'),

    department: Yup.string().required('فیلد دپارتمان گیرنده الزامی است'),

    priority: Yup.string().required('فیلد الویت تیکت الزامی است'),
});
